import React, { useImperativeHandle } from "react";
import { FComptetUser } from "../../../../../../interfaces/UserInterface";
import { InputInterface } from "../../../../../../interfaces/InputInterface";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { requestApi } from "../../../../../../helpers/RequestApi";
import { POST } from "../../../../../../utils/MethodUtils";
import { FCOMPTET_URL } from "../../../../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import getErrorApi from "../../../../../../helpers/GetErrorApi";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { RootState } from "../../../../../../app/store";
import { EN_CPTE_SANS_BDC } from "../../../../../../utils/UserUtils";
import Typography from "@mui/material/Typography";
import { isFiable } from "../../../../../../helpers/UserHelper";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import { set, USER_EN_COMPTE } from "../../../../../../app/globalSlice";

interface State {
  fComptetUser: FComptetUser;
  setFComptetUser: Function;
}

interface FormState {
  fiable: InputInterface;
}

const FiableAcompteComponent = React.memo(
  React.forwardRef(({ fComptetUser, setFComptetUser }: State, ref) => {
    const { t } = useTranslation();
    const getDefaultValues = React.useCallback((): FormState => {
      return {
        fiable: {
          value: fComptetUser?.fiable ?? isFiable(fComptetUser),
          error: "",
        },
      };
    }, [fComptetUser]);
    const [values, setValues] = React.useState<FormState>(getDefaultValues());
    const [loading, setLoading] = React.useState(false);
    const token = useAppSelector((state: RootState) => state.globalState.token);
    const getValue = React.useCallback(() => {
      return {
        fiable: parseInt(values.fiable.value),
      };
    }, [values.fiable.value]);
    const dispatch = useAppDispatch();

    const save = React.useCallback(
      async (realNewValue: boolean | null) => {
        if (
          USER_EN_COMPTE.includes(
            fComptetUser.modeDeCommande ?? EN_CPTE_SANS_BDC
          )
        ) {
          if (realNewValue === true) {
            realNewValue = null;
          }
        } else {
          if (realNewValue === false) {
            realNewValue = null;
          }
        }
        if (fComptetUser.fiable === realNewValue) {
          return;
        }
        setLoading(true);
        const response = await requestApi({
          method: POST,
          path: FCOMPTET_URL + "/" + fComptetUser.userIdentifier + "/update",
          allowError: true,
          token: token,
          body: {
            prop: "fiable",
            value:
              realNewValue === null ? realNewValue : realNewValue ? "1" : "0",
          },
        });
        if (response.statusCode === 200) {
          toastr.success(
            t("word.success"),
            t("sentence.notification.user_updated")
          );
          setFComptetUser((x: FComptetUser | undefined) => {
            if (!x) {
              return x;
            }
            x.fiable =
              realNewValue === null ? realNewValue : realNewValue ? "1" : "0";
            return { ...x };
          });
          dispatch(set({ refreshPage: true }));
        } else if (response.statusCode === 401) {
          toastr.info(t("word.info"), t("error.reconnect"));
        } else {
          for (let message of getErrorApi(response.content)) {
            toastr.error(t("word.error"), t(message));
          }
        }
        setLoading(false);
      },
      [
        dispatch,
        fComptetUser.fiable,
        fComptetUser.modeDeCommande,
        fComptetUser.userIdentifier,
        setFComptetUser,
        t,
        token,
      ]
    );

    useImperativeHandle(ref, () => ({
      getValue() {
        return getValue();
      },
    }));

    React.useEffect(() => {
      setValues(getDefaultValues());
    }, [fComptetUser?.fiable]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography>
          {t("word.modeDeCommande")}: {fComptetUser.modeDeCommande}
        </Typography>
        <FormGroup sx={{ marginLeft: 2 }}>
          {USER_EN_COMPTE.includes(
            fComptetUser.modeDeCommande ?? EN_CPTE_SANS_BDC
          ) ? (
            <FormControlLabel
              control={
                <Checkbox
                  disabled={loading}
                  checked={
                    values.fiable.value === false || values.fiable.value === "0"
                  }
                  onChange={(e) => {
                    setValues((v) => {
                      return {
                        ...v,
                        fiable: {
                          ...v.fiable,
                          value: !e.target.checked,
                          error: "",
                        },
                      };
                    });
                    save(!e.target.checked);
                  }}
                />
              }
              label={t("word.notFiable")}
            />
          ) : (
            <FormControlLabel
              control={
                <Checkbox
                  disabled={loading}
                  checked={values.fiable.value}
                  onChange={(e) => {
                    setValues((v) => {
                      return {
                        ...v,
                        fiable: {
                          ...v.fiable,
                          value: e.target.checked,
                          error: "",
                        },
                      };
                    });
                    save(e.target.checked);
                  }}
                />
              }
              label={t("word.fiable")}
            />
          )}
        </FormGroup>
      </Box>
    );
  })
);

export default FiableAcompteComponent;
